/**
 * Integrytas Group - Document Management System (DMS)
 * Page: DMS.DOCUMENTS
 *
 * Generic document console. Because the repository is application-agnostic, the
 * page simply lets an operator point at any target record (entity name + record
 * id) and manage its documents through the reusable <DocumentManager>. Other
 * modules embed that component directly on their own screens instead.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthSession } from '../../types/auth';
import { DocumentManager } from '../../components/dms/DocumentManager';
import { PersonalizationProvider, PersonalizationToggle } from '../../components/personalization';

export interface DmsDocumentsPageProps {
  session: AuthSession;
}

export function DmsDocumentsPage({ session }: DmsDocumentsPageProps) {
  return (
    <PersonalizationProvider pageCode="DMS.DOCUMENTS" session={session}>
      <DmsDocumentsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function DmsDocumentsPageInner({ session }: DmsDocumentsPageProps) {
  const { t } = useTranslation();
  const [entity, setEntity] = useState('');
  const [record, setRecord] = useState('');
  const [active, setActive] = useState<{ entity: string; record: string } | null>(null);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('dms.title', 'Document Management')}</span>
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="mb-4 max-w-3xl rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-xs text-csm-ink-soft">
            {t('dms.intro_before', 'Documents are linked generically to any business record. Enter the target entity name (e.g.')}
            {' '}<span className="font-mono">fin_invoice</span>{') '}
            {t('dms.intro_after', 'and its record id to manage attachments.')}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dms.target_entity', 'Target Entity')}</label>
              <input
                type="text" value={entity} onChange={e => setEntity(e.target.value)}
                placeholder="fin_invoice"
                className="h-8 w-56 rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('dms.target_record_id', 'Target Record ID')}</label>
              <input
                type="text" value={record} onChange={e => setRecord(e.target.value)}
                placeholder="1024"
                className="h-8 w-56 rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none"
              />
            </div>
            <button
              onClick={() => setActive(entity.trim() && record.trim() ? { entity: entity.trim(), record: record.trim() } : null)}
              disabled={!entity.trim() || !record.trim()}
              className="h-8 rounded border border-sidebar bg-sidebar px-3 text-csm-sm text-white hover:bg-sidebar-alt disabled:opacity-50"
            >{t('dms.open', 'Open')}</button>
          </div>
        </div>

        {active && (
          <div className="max-w-3xl rounded border border-csm-border bg-white p-3">
            <DocumentManager
              targetEntityName={active.entity}
              targetRecordId={active.record}
              session={session}
            />
          </div>
        )}
      </div>
    </div>
  );
}
